import { isEditorActive } from '@sitecore-jss/sitecore-jss-react';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import LocalStorageContext from '../LocalStorageContext/LocalStorageContext';
import safelyStringifyJSON from '../../utils/safelyStringifyJSON';
import { MAX_COMPARE_ELEMENTS } from '../../utils/constants';
import { getParamsFromUrl, updateSearchParams } from './utils';
import { useProductCompareData } from './ProductCompareDataContextProvider';

/** @typedef {{ detId: string, category: { key: string, value: string }}} ReferenceCompareProduct */

const useSyncUrlAndLocalStorage = (activeProducts, setProducts, urlProductParamName) => {
  const history = useHistory();
  const { toCompareProducts, setToCompareProducts, removeFromCompareProducts, replaceToCompareProducts } =
    useContext(LocalStorageContext);
  /** @type {{referenceCompareProducts: ReferenceCompareProduct[]}} */
  const { referenceCompareProducts } = useProductCompareData();
  const referenceCompareProductsMap = new Map(referenceCompareProducts?.map(model => [model.detId, model]));

  const onBackClick = () => {
    if (!isEditorActive()) history.goBack();
  };

  const onRemoveModel = id => {
    if (isEditorActive()) return;
    // sync URL and localStorage with component state
    if (Array.isArray(activeProducts) && activeProducts.length) {
      const update = activeProducts.filter(productId => productId !== id);
      updateSearchParams(urlProductParamName, history, update);
    }
    removeFromCompareProducts(id);
  };

  const onSelectModel = id => {
    if (isEditorActive()) return;
    const validCompareProduct = referenceCompareProductsMap.get(id);
    if (!validCompareProduct) return;
    // sync URL and localStorage with component state
    updateSearchParams(urlProductParamName, history, [...activeProducts, id]);
    setToCompareProducts({
      detId: id,
      [validCompareProduct.category.key]: validCompareProduct.category.value,
    });
  };

  const resetUrlAndLocalStorage = useCallback(ids => {
    if (isEditorActive()) return;
    updateSearchParams(urlProductParamName, history, ids.slice(0, MAX_COMPARE_ELEMENTS));
    replaceToCompareProducts(ids.slice(0, MAX_COMPARE_ELEMENTS));
  }, []);

  /**
   * Updates **toCompareProducts** with products having **detIds** in the URL Search Params
   * @param {string[]} idsFromUrl
   */
  const syncCompareProductsFromURLParamIds = idsFromUrl => {
    const idsFromUrlSet = new Set(idsFromUrl.slice(0, MAX_COMPARE_ELEMENTS));

    const validCompareProducts = referenceCompareProducts.filter(product => idsFromUrlSet.has(product.detId));
    const validIdsSet = new Set(validCompareProducts?.map(product => product.detId));

    const canUpdateCompareProducts =
      !toCompareProducts.length ||
      toCompareProducts.some(product => {
        const categoryKey = referenceCompareProductsMap.get(product.detId)?.category.key ?? '';
        if (!categoryKey) return false;
        /* an empty string ('') is used as the fallback for a missing value in components/CompareProducts/utils.js,
        and *false* is used for products without category restrictions, so a truthiness check isn't enough */
        return (product?.[categoryKey] ?? '') === '' || !validIdsSet.has(product.detId);
      });

    setProducts([...validIdsSet]);

    if (canUpdateCompareProducts) {
      replaceToCompareProducts(
        validCompareProducts?.map(product => ({
          detId: product.detId,
          [product.category.key]: product.category.value,
        })),
      );
    }
  };

  /**
   * First time load: Check if there are params set in the URL
   * and if so, use them to populate and compare the products.
   */
  useEffect(() => {
    /**
     * Check if there are params set in URL, if so, use them as they
     * have priority, otherwise, check if there are params in localStorage,
     * in case there are none, no operation is required.
     */
    const checkUrlAndLocalStorage = () => {
      const idsFromUrl = getParamsFromUrl(urlProductParamName);

      if (Array.isArray(idsFromUrl) && idsFromUrl.length) {
        syncCompareProductsFromURLParamIds(idsFromUrl);
      } else if (Array.isArray(toCompareProducts) && toCompareProducts?.length) {
        setProducts(toCompareProducts?.map(item => item?.detId));
        updateSearchParams(urlProductParamName, history, toCompareProducts);
      }
    };

    checkUrlAndLocalStorage();
  }, [safelyStringifyJSON(toCompareProducts)]);

  return { onRemoveModel, onSelectModel, onBackClick, resetUrlAndLocalStorage };
};

export default useSyncUrlAndLocalStorage;
